<template>
    <div class="w-full h-full flex flex-col p-4">
        <div class="flex items-center mt-2 mb-4 gap-2 justify-between">
            <date-range-selector v-model="dateRange"/>
            <div class="flex gap-2">
                <UPopover v-model:open="isPopoverOpen" :dismissible="true" @close="onClosePopover">
                    <UButton size="sm" @click="lockPeriodButtonClicked">Lås periode</UButton>
                    <template #content>
                        <period-lock-dialog
                            :period="selectedPeriod"
                            :open="openingPeriod"
                            :close="onClosePopover"
                        />
                    </template>
                </UPopover>
            </div>
        </div>
        <div :style="{height: calcGridHeight()+'px'}" class="py-1.5 min-h-[400px] z-0">
            <ag-grid-vue
                :column-defs="columns"
                :row-data="data"
                :read-only-edit="true"
                :grid-options="gridOptions"
                class="w-full h-full"
                :theme="agTheme"
                :master-detail="true"
                :detail-cell-renderer-params="detailCellRendererParams"
                :detail-row-auto-height="true"
                @cell-edit-request="onCellEditRequest"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from 'vue'
import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {CellEditRequestEvent, ColDef, GetDetailRowDataParams, GridOptions, ICellRendererParams} from 'ag-grid-community'
import {globalDefaultColDef} from '@/agUtils'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {endOfYear, startOfYear} from 'date-fns'
import {DateRange} from '@/dateUtils/DateRange'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import {fetchPeriodLogs, Period, PeriodInitOpen, periods, fetchAllPeriods} from '@/period-lock/periodLock.service'
import {clamp} from 'lodash'
import {ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import PeriodLockDialog from '@/period-lock/PeriodLockDialog.vue'
import {PERIOD_CLOSED, PERIOD_OPENED} from '@/types/periodLock/periodLockEvent'
import ToggleLockCellRenderer from '@/period-lock/ToggleLockCellRenderer.vue'

const {onEvents} = useEmitter()

const data = ref<PeriodData[] | PeriodInitOpen[]>([])
const openingPeriod = ref(false)
const isPopoverOpen = ref(false)
const selectedPeriod = ref<Period | undefined>()

type PeriodData = Period & { period: string }

const dateRange = ref<DateRange>(getPersistedRangeOr({
    start: startOfYear(new Date()),
    end: endOfYear(new Date())
}))

const detailCellRendererParams = ref({
    detailGridOptions: {
        columnDefs: [
            {
                field: 'createdAt',
                headerName: 'Dato',
                valueFormatter: (params: any) => new Date(params.value).toLocaleDateString('no')
            },
            {field: 'userEmail', headerName: 'Bruker'},
            {field: 'action', headerName: 'Handling'},
            {field: 'comment', headerName: 'Kommentar'},
        ],
        defaultColDef: {
            flex: 1,
        },
    },
    getDetailRowData: async (params: GetDetailRowDataParams<PeriodData>) => {
        if (!params.data.uuid) {
            params.successCallback([])
            return
        }
        const logs = await getPeriodLogs(params.data.uuid)
        params.successCallback(logs)
    }
})

const lockPeriodButtonClicked = () => {
    openingPeriod.value = false
    selectedPeriod.value = undefined
    isPopoverOpen.value = true
}

const displayPeriodLockDialog = (period?: Period) => {
    openingPeriod.value = false
    selectedPeriod.value = period
    isPopoverOpen.value = true
}

const displayPeriodOpenDialog = (period: Period) => {
    openingPeriod.value = true
    selectedPeriod.value = period
    isPopoverOpen.value = true
}

const onClosePopover = () => {
    isPopoverOpen.value = false
}

const getPeriods = async () => {
    await fetchAllPeriods()
    data.value = periods.value.filter((period: Period) => {
        const periodDate = new Date(period.year, period.month - 1, 1, 1)
        return periodDate >= dateRange.value.start && periodDate <= dateRange.value.end
    }).map((period: Period) => {
        const date = new Date(period.year, period.month - 1)
        const dateString = date.toLocaleString('no', {month: 'long', year: 'numeric'})
        return {
            ...period,
            period: dateString.charAt(0).toUpperCase() + dateString.slice(1)
        } as PeriodData
    })
    addMissingOpenPeriods()
    sortMonthColumn()
}

const addMissingOpenPeriods = () => {
    const startYear = dateRange.value.start.getFullYear()
    const endYear = dateRange.value.end.getFullYear()
    const startMonth = dateRange.value.start.getMonth() + 1
    const endMonth = endYear < new Date().getFullYear() ? 12 : (endYear === new Date().getFullYear() ? new Date().getMonth() : 0)

    const openPeriods = []
    for (let year = startYear; year <= endYear; year++) {
        const [start, end] = [
            year === startYear ? startMonth : 1,
            year === endYear ? endMonth : 12
        ]
        for (let month = start; month <= end; month++) {
            const periodString = new Date(year, month - 1).toLocaleString('no', {month: 'long', year: 'numeric'})
            openPeriods.push({
                period: periodString.charAt(0).toUpperCase() + periodString.slice(1),
                isClosed: false,
                month: month,
                year: year
            } as PeriodInitOpen)
        }
    }

    data.value = [
        ...data.value,
        ...openPeriods.filter((openPeriod: PeriodInitOpen) => !data.value.some((period) => period.month === openPeriod.month && period.year === openPeriod.year))
    ]
}

const sortMonthColumn = () => {
    data.value.sort((a, b) => {
        const dateA = new Date(a.year, a.month - 1).getTime()
        const dateB = new Date(b.year, b.month - 1).getTime()
        return dateA - dateB
    })
}

const getPeriodLogs = async (periodUuid: string) => {
    return await fetchPeriodLogs(periodUuid)
}

const onCellEditRequest = (event: CellEditRequestEvent<PeriodData, boolean>) => {
    if (event.value === false) displayPeriodOpenDialog(event.data)
    else displayPeriodLockDialog(event.data)
}

const calcGridHeight = () => {
    const cappedlineCount = clamp(data.value.length, 0, 50)
    return cappedlineCount * 28 + 177
}

onMounted(() => {
    getPeriods()
})

onEvents([ORG_SELECTED, ORG_SWITCHED, PERIOD_OPENED, PERIOD_CLOSED], async () => {
    await getPeriods()
})

watch(dateRange, () => {
    getPeriods()
})

const columns: ColDef[] = [
    {
        headerName: 'Måned',
        field: 'period',
        cellRenderer: 'agGroupCellRenderer',
        sortable: false
    },
    {
        headerName: 'Låst',
        field: 'isClosed',
        cellStyle: { display:'flex', alignItems: 'center'},
        cellRenderer: ToggleLockCellRenderer,
        cellRendererParams: (params: ICellRendererParams) => {
            return {
                isClosed: params.data.isClosed,
                period: params.data
            }
        }
    },
    {
        headerName: 'Siste endring',
        field: 'updatedAt',
        valueFormatter: (params) => {
            if (!params.value) return ''
            return new Date(params.value).toLocaleDateString('no')
        },
    },
    {
        headerName: 'Siste kommentar',
        field: 'latestComment',
    },
    {
        headerName: 'Bruker',
        field: 'user',
    },
]

const gridOptions: GridOptions = {
    animateRows: true,
    suppressDragLeaveHidesColumns: true,
    getRowId: (params) => {
        return params.data.period
    },
    defaultColDef: {
        ...globalDefaultColDef,
        flex: 1,
        sortable: true,
        resizable: true,
    }
}

</script>

<style scoped>

</style>